import * as R from "ramda";
import { Action, combineReducers } from "redux";
import { Action as ReduxAction, handleActions, ReducerMap } from "redux-actions";
import { IAction } from "reduxsauce";
import { Types as AllTypes } from "../../actions";
import * as ActionsCourseAuthor from "../../actions/course-author/course-author";
import { CourseChanges } from "../../components/course-author/helpers/patches";
import { IFullCourse } from "../../components/course-author/types";
import { patchCourseReducer } from "./patch-course";
import { ICourseAuthorState, IOrganization, IState } from "./types";

export enum RequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

const Types = (AllTypes as any)._APP._COURSE_AUTHOR;

export const INITIAL_STATE: ICourseAuthorState = {
  administrableOrgs: [],
  autocompleteApi: {} as any,
  cloneCourseRequestError: null,
  cloneCourseRequestState: RequestState.IDLE,
  course: null,
  courseChanges: [],
  courseId: null,
  courseInstId: null,
  createStyleRequestError: null,
  createStyleRequestState: RequestState.IDLE,
  displayLPWarning: false,
  displayPreviewCourseInst: false,
  displayPreviewCourseInstFailMessage: null,
  fetchAllOrganizationsRequestError: null,
  fetchAllOrganizationsRequestState: RequestState.IDLE,
  fetchCourseChangesRequestError: null,
  fetchCourseChangesRequestState: RequestState.IDLE,
  fetchCourseRequestError: null,
  fetchCourseRequestState: RequestState.IDLE,
  fileUploadApi: {} as any,
  importReport: null,
  localizedMessageApi: {} as any,
  migrateContentToHtmlRequestError: null,
  migrateContentToHtmlRequestState: RequestState.IDLE,
  organizations: null,
  patchCourseImportTranslationsCsvFailure: null,
  patchCoursePlayerRequestError: null,
  patchCoursePlayerRequestState: RequestState.IDLE,
  patchCourseRequestError: null,
  patchCourseRequestState: RequestState.IDLE,
  patchStyleRequestError: null,
  patchStyleRequestState: RequestState.IDLE,
  requestPreviewCourseInstError: null,
  requestPreviewCourseInstState: RequestState.IDLE,
  resolveAuxiliaryRequestError: null,
  resolveAuxiliaryRequestState: RequestState.IDLE,
  resolvedData: {
    admins: [],
    editors: [],
    eligibleEditors: [],
    mediaFiles: [],
  },
  updateCourseRequestError: null,
  updateCourseRequestState: RequestState.IDLE,
};

export function mount(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...INITIAL_STATE,
    courseId: action.courseId || INITIAL_STATE.courseId,
  };
}

export function unmount(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return INITIAL_STATE;
}

export function fetchAllOrganizations(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    fetchAllOrganizationsRequestError: null,
    fetchAllOrganizationsRequestState: RequestState.PENDING,
  };
}

export function handleFetchAllOrganizationsFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    fetchAllOrganizationsRequestError: action.error.message,
    fetchAllOrganizationsRequestState: RequestState.FAILED,
  };
}

export function handleFetchAllOrganizationsSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    fetchAllOrganizationsRequestError: null,
    fetchAllOrganizationsRequestState: RequestState.SUCCEEDED,
    organizations: action.organizations,
  };
}

export function fetchCourse(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    fetchCourseRequestError: null,
    fetchCourseRequestState: RequestState.PENDING,
  };
}

export function handleFetchCourseFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  if (state.updateCourseRequestState === RequestState.PENDING) {
    return {
      ...state,
      fetchCourseRequestError: action.error.message,
      fetchCourseRequestState: RequestState.FAILED,
      updateCourseRequestState: RequestState.FAILED,
    };
  }

  return {
    ...state,
    fetchCourseRequestError: action.error.message,
    fetchCourseRequestState: RequestState.FAILED,
  };
}

export function handleFetchCourseSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  if (state.updateCourseRequestState === RequestState.PENDING) {
    return {
      ...state,
      autocompleteApi: action.autocompleteApi,
      course: action.course,
      fetchCourseRequestError: null,
      fetchCourseRequestState: RequestState.SUCCEEDED,
      fileUploadApi: action.fileUploadApi,
      localizedMessageApi: action.localizedMessageApi,
      updateCourseRequestState: RequestState.SUCCEEDED,
    };
  }

  return {
    ...state,
    autocompleteApi: action.autocompleteApi,
    course: action.course,
    fetchCourseRequestError: null,
    fetchCourseRequestState: RequestState.SUCCEEDED,
    fileUploadApi: action.fileUploadApi,
    localizedMessageApi: action.localizedMessageApi,
  };
}

export function patchCourse(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchCourseRequestError: null,
    patchCourseRequestState: RequestState.PENDING,
    updateCourseRequestState: RequestState.PENDING,
  };
}

export function simplePatchCourse(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchCourseRequestError: null,
    patchCourseRequestState: RequestState.PENDING,
    updateCourseRequestState: RequestState.PENDING,
  };
}

export function handlePatchCourseFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchCourseRequestError: action.error.message,
    patchCourseRequestState: RequestState.FAILED,
    updateCourseRequestState: RequestState.FAILED,
  };
}

export function handlePatchCourseSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchCourseRequestError: null,
    patchCourseRequestState: RequestState.SUCCEEDED,
  };
}

export function updateFullCourse(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    updateCourseRequestError: null,
    updateCourseRequestState: RequestState.PENDING,
  };
}

export function handleUpdateFullCourseFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    updateCourseRequestError: action.error.message,
    updateCourseRequestState: RequestState.FAILED,
  };
}

export function handleUpdateFullCourseSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    courseChanges: action.changes,
    resolvedData: {
      ...state.resolvedData,
      admins: action.admins,
      editors: action.editors,
    },
    updateCourseRequestError: null,
    updateCourseRequestState: RequestState.SUCCEEDED,
  };
}

export function handleResolveAuxiliarySuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    resolvedData: {
      ...state.resolvedData,
      [action.key]: action.value,
    },
  };
}

export function createStyle(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    createStyleRequestError: null,
    createStyleRequestState: RequestState.PENDING,
    updateCourseRequestState: RequestState.PENDING,
  };
}

export function handleCreateStyleFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    createStyleRequestError: action.error.message,
    createStyleRequestState: RequestState.FAILED,
    updateCourseRequestState: RequestState.FAILED,
  };
}

export function handlecreateStyleSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    createStyleRequestError: null,
    createStyleRequestState: RequestState.SUCCEEDED,
  };
}

export function patchStyle(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchStyleRequestError: null,
    patchStyleRequestState: RequestState.PENDING,
    updateCourseRequestState: RequestState.PENDING,
  };
}

export function handlePatchStyleFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchStyleRequestError: action.error.message,
    patchStyleRequestState: RequestState.FAILED,
    updateCourseRequestState: RequestState.FAILED,
  };
}

export function handlePatchStyleSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    patchStyleRequestError: null,
    patchStyleRequestState: RequestState.SUCCEEDED,
  };
}

export function addImmersiveBg(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    updateCourseRequestError: null,
    updateCourseRequestState: RequestState.PENDING,
  };
}

export function handleAddImmBgSuccess(state: ICourseAuthorState): ICourseAuthorState {
  return {
    ...state,
    updateCourseRequestError: null,
    updateCourseRequestState: RequestState.SUCCEEDED,
  };
}

export function handleAddImmBgFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    updateCourseRequestError: action.error.message,
    updateCourseRequestState: RequestState.FAILED,
  };
}

export function cloneCourse(state: ICourseAuthorState): ICourseAuthorState {
  return {
    ...state,
    cloneCourseRequestError: null,
    cloneCourseRequestState: RequestState.PENDING,
  };
}

const handleCloneCourseFailure = (state: ICourseAuthorState, action: IAction): ICourseAuthorState => ({
  ...state,
  cloneCourseRequestError: action.error.message,
  cloneCourseRequestState: RequestState.FAILED,
});

const handleCloneCourseSuccess = (state: ICourseAuthorState, action: IAction): ICourseAuthorState => ({
  ...state,
  cloneCourseRequestError: null,
  cloneCourseRequestState: RequestState.SUCCEEDED,
});

export const migrateContentToHtml = (state: ICourseAuthorState, action: IAction): ICourseAuthorState => ({
  ...state,
  migrateContentToHtmlRequestError: null,
  migrateContentToHtmlRequestState: RequestState.PENDING,
});

export const handleMigrateContentToHtmlSuccess = (state: ICourseAuthorState, action: IAction): ICourseAuthorState => {
  return {
    ...state,
    course: action.course,
    migrateContentToHtmlRequestError: null,
    migrateContentToHtmlRequestState: RequestState.SUCCEEDED,
  };
};

export const handleMigrateContentToHtmlFailure = (state: ICourseAuthorState, action: IAction): ICourseAuthorState => ({
  ...state,
  migrateContentToHtmlRequestError: action.error.message,
  migrateContentToHtmlRequestState: RequestState.FAILED,
});
export function fetchCourseChanges(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    fetchCourseChangesRequestError: null,
    fetchCourseChangesRequestState: RequestState.PENDING,
  };
}

export function handleFetchCourseChangesFailure(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  return {
    ...state,
    fetchCourseChangesRequestError: action.error.message,
    fetchCourseChangesRequestState: RequestState.FAILED,
  };
}

export function handleFetchCourseChangesSuccess(state: ICourseAuthorState, action: IAction): ICourseAuthorState {
  const mergeFn = R.compose<any, CourseChanges[], CourseChanges, CourseChanges, CourseChanges, CourseChanges>(
    R.reverse,
    R.sortBy(R.prop("versionId")),
    R.uniqBy(R.prop("versionId")),
    R.flatten as any,
    R.map<any, CourseChanges>(R.propOr<CourseChanges[]>([], "courseChanges"))
  );
  const courseChanges: CourseChanges = mergeFn([state, action]);
  return {
    ...state,
    courseChanges,
    fetchCourseChangesRequestError: null,
    fetchCourseChangesRequestState: RequestState.SUCCEEDED,
  };
}

export const patchCoursePlayer = (state: ICourseAuthorState, _: IAction): ICourseAuthorState => ({
  ...state,
  patchCoursePlayerRequestError: null,
  patchCoursePlayerRequestState: RequestState.PENDING,
});

export const handlePatchCoursePlayerSuccess = (state: ICourseAuthorState, _: IAction): ICourseAuthorState => ({
  ...state,
  patchCoursePlayerRequestError: null,
  patchCoursePlayerRequestState: RequestState.SUCCEEDED,
});

export const handlePatchCoursePlayerFailure = (state: ICourseAuthorState, action: IAction): ICourseAuthorState => ({
  ...state,
  patchCoursePlayerRequestError: action.error.message,
  patchCoursePlayerRequestState: RequestState.FAILED,
});

const requestPreviewCourseInst = (state: ICourseAuthorState): ICourseAuthorState => ({
  ...state,
  courseInstId: null,
  displayPreviewCourseInst: false,
  requestPreviewCourseInstState: RequestState.PENDING,
});

const hideLPWarning = (state: ICourseAuthorState): ICourseAuthorState => ({
  ...state,
  displayLPWarning: false,
});

const resetPreviewCourseInst = (state: ICourseAuthorState): ICourseAuthorState => ({
  ...state,
  courseInstId: null,
  displayLPWarning: false,
  displayPreviewCourseInst: false,
  requestPreviewCourseInstState: RequestState.IDLE,
});

const handleRequestPreviewCourseInstSuccess = (
  state: ICourseAuthorState,
  action: ReduxAction<string>
): ICourseAuthorState => ({
  ...state,
  courseInstId: action.payload,
  displayLPWarning: true,
  displayPreviewCourseInst: true,
  requestPreviewCourseInstState: RequestState.SUCCEEDED,
});

const handleRequestPreviewCourseInstFailure = (
  state: ICourseAuthorState,
  action: ReduxAction<{ message: string }>
): ICourseAuthorState => ({
  ...state,
  courseInstId: null,
  displayPreviewCourseInst: false,
  displayPreviewCourseInstFailMessage: action.payload.message,
  requestPreviewCourseInstState: RequestState.FAILED,
});

const fetchAdministableOrgs = (state: ICourseAuthorState): ICourseAuthorState => ({
  ...state,
  administrableOrgs: [],
});

const handleFetchAllDashboardDataSuccess = (
  state: ICourseAuthorState,
  action: ReduxAction<IOrganization[]>
): ICourseAuthorState => ({
  ...state,
  administrableOrgs: action.payload,
});

const handleFetchAllDashboardDataFailure = (state: ICourseAuthorState): ICourseAuthorState => ({
  ...state,
  administrableOrgs: [],
});

const handleFetchFullCourseSuccess = (
  state: ICourseAuthorState,
  action: ReduxAction<IFullCourse>
): ICourseAuthorState => ({
  ...state,
  course: action.payload,
});

const reduxActionReducers: ReducerMap<ICourseAuthorState, any> = {
  [ActionsCourseAuthor.REQUEST_PREVIEW_COURSE_INST]: requestPreviewCourseInst,
  [ActionsCourseAuthor.RESET_PREVIEW_COURSE_INST]: resetPreviewCourseInst,
  [ActionsCourseAuthor.HIDE_LINEAR_PROGRESSION_WARNING]: hideLPWarning,
  [ActionsCourseAuthor.HANDLE_REQUEST_PREVIEW_COURSE_INST_SUCCESS]: handleRequestPreviewCourseInstSuccess,
  [ActionsCourseAuthor.HANDLE_REQUEST_PREVIEW_COURSE_INST_FAILURE]: handleRequestPreviewCourseInstFailure,
  [ActionsCourseAuthor.FETCH_ADMINISTRABLE_ORGS]: fetchAdministableOrgs,
  [ActionsCourseAuthor.HANDLE_FETCH_ADMINISTRABLE_ORGS_SUCCESS]: handleFetchAllDashboardDataSuccess,
  [ActionsCourseAuthor.HANDLE_FETCH_ADMINISTRABLE_ORGS_FAILURE]: handleFetchAllDashboardDataFailure,
  [ActionsCourseAuthor.HANDLE_FETCH_FULL_COURSE_SUCCESS]: handleFetchFullCourseSuccess,
};

const saucesReducers: ReducerMap<ICourseAuthorState, any> = {
  [Types.ADD_IMMERSIVE_BACKGROUND]: addImmersiveBg,
  [Types.CLONE_COURSE]: cloneCourse,
  [Types.CREATE_STYLE]: createStyle,
  [Types.FETCH_ALL_ORGANIZATIONS]: fetchAllOrganizations,
  [Types.FETCH_COURSE_CHANGES]: fetchCourseChanges,
  [Types.FETCH_COURSE]: fetchCourse,
  [Types.HANDLE_ADD_IMM_BG_FAILURE]: handleAddImmBgFailure,
  [Types.HANDLE_ADD_IMM_BG_SUCCESS]: handleAddImmBgSuccess,
  [Types.HANDLE_CLONE_COURSE_FAILURE]: handleCloneCourseFailure,
  [Types.HANDLE_CLONE_COURSE_SUCCESS]: handleCloneCourseSuccess,
  [Types.HANDLE_CREATE_STYLE_FAILURE]: handleCreateStyleFailure,
  [Types.HANDLE_CREATE_STYLE_SUCCESS]: handlecreateStyleSuccess,
  [Types.HANDLE_FETCH_ALL_ORGANIZATIONS_FAILURE]: handleFetchAllOrganizationsFailure,
  [Types.HANDLE_FETCH_ALL_ORGANIZATIONS_SUCCESS]: handleFetchAllOrganizationsSuccess,
  [Types.HANDLE_FETCH_COURSE_CHANGES_FAILURE]: handleFetchCourseChangesFailure,
  [Types.HANDLE_FETCH_COURSE_CHANGES_SUCCESS]: handleFetchCourseChangesSuccess,
  [Types.HANDLE_FETCH_COURSE_FAILURE]: handleFetchCourseFailure,
  [Types.HANDLE_FETCH_COURSE_SUCCESS]: handleFetchCourseSuccess,
  [Types.HANDLE_MIGRATE_CONTENT_TO_HTML_FAILURE]: handleMigrateContentToHtmlFailure,
  [Types.HANDLE_MIGRATE_CONTENT_TO_HTML_SUCCESS]: handleMigrateContentToHtmlSuccess,
  [Types.HANDLE_PATCH_COURSE_FAILURE]: handlePatchCourseFailure,
  [Types.HANDLE_PATCH_COURSE_SUCCESS]: handlePatchCourseSuccess,
  [Types.HANDLE_PATCH_COURSE_PLAYER_FAILURE]: handlePatchCoursePlayerFailure,
  [Types.HANDLE_PATCH_COURSE_PLAYER_SUCCESS]: handlePatchCoursePlayerSuccess,
  [Types.HANDLE_PATCH_STYLE_FAILURE]: handlePatchStyleFailure,
  [Types.HANDLE_PATCH_STYLE_SUCCESS]: handlePatchStyleSuccess,
  [Types.HANDLE_RESOLVE_AUXILIARY_SUCCESS]: handleResolveAuxiliarySuccess,
  [Types.HANDLE_UPDATE_FULL_COURSE_FAILURE]: handleUpdateFullCourseFailure,
  [Types.HANDLE_UPDATE_FULL_COURSE_SUCCESS]: handleUpdateFullCourseSuccess,
  [Types.MIGRATE_CONTENT_TO_HTML]: migrateContentToHtml,
  [Types.MOUNT]: mount,
  [Types.PATCH_COURSE]: patchCourse,
  [Types.SIMPLE_PATCH_COURSE]: simplePatchCourse,
  [Types.PATCH_COURSE_PLAYER]: patchCoursePlayer,
  [Types.PATCH_STYLE]: patchStyle,
  [Types.UPDATE_FULL_COURSE]: updateFullCourse,
};

export const courseAuthorReducer = combineReducers<IState, Action>({
  _: handleActions(
    {
      ...saucesReducers,
      ...patchCourseReducer,
      ...reduxActionReducers,
    },
    INITIAL_STATE
  ),
});
